import { prescriptionIdentifier } from "@/utils/prescriptionIdentity.js";
import {
  buildThanksReply,
  buildPrescriptionStatusReply,
  classifyFolaSupportIntent,
} from "@/lib/folaSupportGrounding.mjs";

const FULFILLMENT_DESCRIPTION_PREFIX = "prescription_fulfillment:";

const STATUS_QUERY_KEYWORDS = ["update", "status", "ready", "collect", "pickup"];
const STATUS_QUERY_PATTERNS = [
  /\bwhere\s+is\s+(my\s+)?prescription\b/i,
  /\bwhen\s+can\s+i\s+(collect|pick\s*up)\b/i,
];

function normalizeText(value) {
  return String(value || "").trim();
}

export function isFulfillmentConversationDescription(description) {
  return normalizeText(description).toLowerCase().startsWith(FULFILLMENT_DESCRIPTION_PREFIX);
}

export function parseFulfillmentPrescriptionId(description) {
  const cleanDescription = normalizeText(description);
  if (!isFulfillmentConversationDescription(cleanDescription)) return "";
  return cleanDescription.slice(FULFILLMENT_DESCRIPTION_PREFIX.length).trim();
}

export function classifyFulfillmentMessageIntent(messageText) {
  const normalized = normalizeText(messageText).toLowerCase();
  if (!normalized) return "FULFILLMENT_UNKNOWN";

  const groundedIntent = classifyFolaSupportIntent(normalized);
  if (groundedIntent === "thanks") return "FULFILLMENT_THANKS";
  if (groundedIntent === "prescription_status") return "FULFILLMENT_STATUS_QUERY";

  if (
    STATUS_QUERY_KEYWORDS.some((keyword) => normalized.includes(keyword)) ||
    STATUS_QUERY_PATTERNS.some((pattern) => pattern.test(normalized))
  ) {
    return "FULFILLMENT_STATUS_QUERY";
  }

  return "FULFILLMENT_UNKNOWN";
}

export async function fetchFulfillmentReplySnapshot({ supabase, prescriptionId }) {
  const cleanPrescriptionId = normalizeText(prescriptionId);
  if (!cleanPrescriptionId) {
    return {
      prescriptionId: "",
      prescriptionReference: "",
      prescriptionStatus: "",
      status: "pending_selection",
      mode: "pickup",
      updatedAt: "",
      pharmacyName: "",
    };
  }

  const [prescriptionResult, fulfillmentResult] = await Promise.all([
    supabase
      .from("prescriptions")
      .select("id,external_rx_id,status,meta,updated_at,created_at")
      .eq("id", cleanPrescriptionId)
      .maybeSingle(),
    supabase
      .from("prescription_fulfillments")
      .select("*")
      .eq("prescription_id", cleanPrescriptionId)
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle(),
  ]);

  const { data: prescriptionRow, error: prescriptionError } = prescriptionResult;
  const { data: fulfillmentRow, error: fulfillmentError } = fulfillmentResult;

  if (
    prescriptionError &&
    !["PGRST205", "42P01", "42501"].includes(String(prescriptionError?.code || ""))
  ) {
    throw prescriptionError;
  }

  if (
    fulfillmentError &&
    !["PGRST205", "42P01", "42501"].includes(String(fulfillmentError?.code || ""))
  ) {
    throw fulfillmentError;
  }

  let pharmacyName = "";
  const pharmacyOwnerUserId = normalizeText(fulfillmentRow?.pharmacy_id);
  if (pharmacyOwnerUserId) {
    const { data: pharmacyProfile, error: pharmacyError } = await supabase
      .from("pharmacy_connect_profiles")
      .select("pharmacy_name, trading_name")
      .eq("user_id", pharmacyOwnerUserId)
      .maybeSingle();

    if (
      pharmacyError &&
      !["PGRST205", "42P01", "42501"].includes(String(pharmacyError?.code || ""))
    ) {
      throw pharmacyError;
    }

    pharmacyName = normalizeText(
      pharmacyProfile?.trading_name || pharmacyProfile?.pharmacy_name
    );
  }

  return {
    prescriptionId: cleanPrescriptionId,
    prescriptionReference:
      prescriptionIdentifier(prescriptionRow || {}),
    prescriptionStatus: normalizeText(prescriptionRow?.status).toLowerCase(),
    status: normalizeText(fulfillmentRow?.status).toLowerCase() || "pending_selection",
    mode: normalizeText(fulfillmentRow?.mode).toLowerCase() || "pickup",
    updatedAt: normalizeText(fulfillmentRow?.updated_at || fulfillmentRow?.created_at),
    pharmacyName,
  };
}

function resolveSemanticFulfillmentStatus(status) {
  const normalizedStatus = normalizeText(status).toLowerCase();

  if (!normalizedStatus || ["pending_selection", "not_started"].includes(normalizedStatus)) {
    return "pending_selection";
  }

  if (["priced", "awaiting_payment"].includes(normalizedStatus)) {
    return "priced";
  }

  if (normalizedStatus === "ready_for_pickup") {
    return "ready_for_pickup";
  }

  return "reviewing";
}

export function buildFulfillmentFolaReply({ intent, snapshot, t }) {
  if (intent === "FULFILLMENT_THANKS") {
    return {
      kind: "fulfillment_acknowledgement",
      semanticStatus: resolveSemanticFulfillmentStatus(snapshot?.status),
      text: buildThanksReply(),
    };
  }

  const semanticStatus = resolveSemanticFulfillmentStatus(snapshot?.status);
  const pharmacyName =
    normalizeText(snapshot?.pharmacyName) ||
    t("messages.conversation.fulfillmentFola.selectedPharmacy");

  if (intent !== "FULFILLMENT_STATUS_QUERY") {
    return {
      kind: "fulfillment_help",
      semanticStatus,
      text: t("messages.conversation.fulfillmentFola.responses.fallback"),
    };
  }

  const groundedStatusReply = buildPrescriptionStatusReply({
    prescription: snapshot?.prescriptionId
      ? {
          id: snapshot.prescriptionId,
          reference: snapshot.prescriptionReference,
          status: snapshot.prescriptionStatus,
        }
      : null,
    fulfillment: snapshot?.status && snapshot.status !== "pending_selection"
      ? {
          status: snapshot.status,
          pharmacyName: snapshot.pharmacyName,
        }
      : null,
  });

  if (groundedStatusReply) {
    return {
      kind: "fulfillment_status_grounded",
      semanticStatus,
      text: groundedStatusReply,
    };
  }

  if (semanticStatus === "pending_selection") {
    return {
      kind: "fulfillment_status_pending_selection",
      semanticStatus,
      text: t("messages.conversation.fulfillmentFola.responses.pendingSelection"),
    };
  }

  if (semanticStatus === "priced") {
    return {
      kind: "fulfillment_status_priced",
      semanticStatus,
      text: t("messages.conversation.fulfillmentFola.responses.priced"),
    };
  }

  if (semanticStatus === "ready_for_pickup") {
    return {
      kind: "fulfillment_status_ready_for_pickup",
      semanticStatus,
      text: t("messages.conversation.fulfillmentFola.responses.readyForPickup", "", {
        pharmacy_name: pharmacyName,
      }),
    };
  }

  return {
    kind: "fulfillment_status_reviewing",
    semanticStatus,
    text: t("messages.conversation.fulfillmentFola.responses.reviewing", "", {
      pharmacy_name: pharmacyName,
    }),
  };
}
